import React from "react";
import { useNavigate } from "react-router-dom";
import CancelButton from "./cancel-button";
import Button from "./button";
import { OwnerState, ViewState } from "@/types/owner-edit";
import { ItemFileData } from "@/types";
import { calcLevelByExperience } from "@/utils/experience";
import { LevelGradientProgressBar } from "./level-gradient-progress-bar";
import { backend } from "@/services/backend";

interface InfoModalProps {
  onClose: () => void;
  ownerData: OwnerState;
  address: string;
  viewState: ViewState;
}

export const InfoModal = ({
  onClose,
  ownerData,
  address,
  viewState,
}: InfoModalProps) => {
  const navigate = useNavigate();
  const experience = ownerData.extendInfo?.experience?.experience;
  const level = calcLevelByExperience(experience || 0);
  const [rank, setRank] = React.useState<number | null>(null);

  React.useEffect(() => {
    const fetchRank = async () => {
      try {
        const rank = await backend.getWalletRatingPosition(
          address,
          "experience"
        );
        setRank(rank || null);
      } catch (error) {
        console.error("Error fetching rank:", error);
      }
    };
    fetchRank();
  }, [address]);

  const handleRankClick = () => {
    navigate("/leaderboard/users/level");
  };

  const creationDate = ownerData.extendInfo?.date_created
    ? new Date(ownerData.extendInfo.date_created).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : "N/A";

  const [itemDataAvatar, setItemDataAvatar] =
    React.useState<ItemFileData | null>(null);
  const [itemDataBackground, setItemDataBackground] =
    React.useState<ItemFileData | null>(null);

  React.useEffect(() => {
    const itemUrlAvatar = ownerData.extendInfo?.avatar_data_url;
    const itemUrlBg = ownerData.extendInfo?.background_data_url;

    if (itemUrlAvatar) {
      fetch(itemUrlAvatar)
        .then(res => res.json())
        .then(res => {
          setItemDataAvatar(res);
        })
        .catch(console.error);
    }
    if (itemUrlBg) {
      fetch(itemUrlBg)
        .then(res => res.json())
        .then(res => {
          setItemDataBackground(res);
        })
        .catch(console.error);
    }
  }, [ownerData.extendInfo]);

  return (
    <div className="flex flex-col bg-black text-white">
      <div className="flex justify-between items-center px-[20px] py-[12px] bg-primary-blue w-full ">
        <span className="text-[33px]">INFORMATION</span>
        <CancelButton
          className="absolute right-[20px] w-[29px] h-[29px]"
          color="white"
          onClick={onClose}
        />
      </div>

      <div className="flex flex-col w-full overflow-y-auto p-[20px] text-white">
        <div className="flex flex-col  text-[24px]">
          <div className="flex justify-between">
            <div className="flex gap-[10px] text-[32px] text-primary-blue">
              <span>LEVEL:</span>
              <span>{level}</span>
            </div>
            <div
              className="flex gap-[10px] text-[16px] text-[#999999] items-end mb-[10px] border-b border-[#999999] cursor-pointer hover:text-primary-blue hover:border-primary-blue transition-colors duration-200"
              onClick={handleRankClick}
            >
              <span>rank:</span>
              <span>{rank ?? "No rank"}</span>
            </div>
          </div>
          {viewState.showOwnerView && viewState.isOwnerView && (
            <LevelGradientProgressBar
              exp={experience || 0}
              className="mb-[15px]"
            />
          )}
          <div className="flex flex-col gap-[10px]">
            {ownerData.extendInfo?.background_data_url !== null ? (
              <>
                <div className="flex gap-[10px]">
                  <span>BACKGROUND:</span>
                  <span className="font-spline-sans-mono">
                    {ownerData.extendInfo?.background_index_in !== null &&
                    ownerData.extendInfo?.background_index_in !== undefined
                      ? "NFT"
                      : "Custom"}
                  </span>
                </div>

                {ownerData.extendInfo?.background_index_in !== null &&
                  ownerData.extendInfo?.background_index_in !== undefined && (
                    <div className="flex gap-[10px] text-[16px] text-gray-400">
                      <span className="font-spline-sans-mono">Number:</span>
                      <span className="font-spline-sans-mono">
                        #{ownerData.extendInfo?.background_index_in}
                      </span>{" "}
                    </div>
                  )}

                {itemDataBackground && (
                  <div className="text-[16px] text-gray-400">
                    {itemDataBackground.attributes.map((item, index) => {
                      return (
                        <div
                          className="font-spline-sans-mono"
                          key={index}
                        >{`${item.trait_type}: ${item.value}`}</div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="flex gap-[10px]">
                <span>BACKGROUND:</span>
                <span className="font-spline-sans-mono">None</span>
              </div>
            )}
            {ownerData.extendInfo?.avatar_data_url !== null ? (
              <>
                <div className="flex gap-[10px]">
                  <span>AVATAR:</span>
                  <span className="font-spline-sans-mono">
                    {ownerData.extendInfo?.avatar_index_in !== null &&
                    ownerData.extendInfo?.avatar_index_in !== undefined
                      ? "NFT"
                      : "Custom"}
                  </span>
                </div>
                {ownerData.extendInfo?.avatar_index_in !== null &&
                  ownerData.extendInfo?.avatar_index_in !== undefined && (
                    <div className="flex gap-[10px] text-[16px] text-gray-400">
                      <span className="font-spline-sans-mono">Number:</span>

                      <span className="font-spline-sans-mono">
                        #{ownerData.extendInfo?.avatar_index_in}
                      </span>
                    </div>
                  )}

                {itemDataAvatar && (
                  <div className="text-[16px] text-gray-400">
                    {itemDataAvatar.attributes.map((item, index) => {
                      return (
                        <div
                          className="font-spline-sans-mono"
                          key={index}
                        >{`${item.trait_type}: ${item.value}`}</div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="flex gap-[10px]">
                <span>AVATAR:</span>
                <span className="font-spline-sans-mono">None</span>
              </div>
            )}
            <div className="flex gap-[10px]">
              <span>REFERRALS:</span>
              <span className="font-spline-sans-mono">
                {ownerData.extendInfo?.referral_count}
              </span>
            </div>

            <div className="flex gap-[10px] text-gray-400 text-[16px]">
              <span className="font-spline-sans-mono">Creation Date:</span>
              <span className="font-spline-sans-mono">{creationDate}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center pt-[40px] pb-[30px]">
          <Button
            className="custom-aspect-320-63 text-[24px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
            image="/assets/button.svg"
            text="OK"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
};
